import { IconButton } from '@mui/material';
import { CustomContentProps, SnackbarContent, VariantType, closeSnackbar } from 'notistack';
import { ReactNode, forwardRef, useCallback } from 'react';
import { ReactComponent as CheckCircle } from '../../../assets/icons/checkbox-circle-fill.svg';
import { ReactComponent as Close } from '../../../assets/icons/close-line.svg';
import { ReactComponent as ErrorRounded } from '../../../assets/icons/error-warning-fill.svg';
import { ReactComponent as Info } from '../../../assets/icons/information-fill.svg';
import classes from './Snackbar.module.scss';

interface NBSnackbarProps extends CustomContentProps {
  title: string;
}

const snackbarClasses: { [key in VariantType]: string } = {
  default: classes.snackbarIsInfo,
  error: classes.snackbarIsError,
  info: classes.snackbarIsInfo,
  success: classes.snackbarIsSuccess,
  warning: classes.snackbarIsWarning,
};

const pinnedSnackbarClasses: { [key in VariantType]: string } = {
  default: classes.snackbarIsInfoIsPinned,
  error: classes.snackbarIsErrorIsPinned,
  info: classes.snackbarIsInfoIsPinned,
  success: classes.snackbarIsSuccessIsPinned,
  warning: classes.snackbarIsWarningIsPinned,
};

const icon: { [key in VariantType]: ReactNode } = {
  default: <Info />,
  error: <ErrorRounded />,
  info: <Info />,
  success: <CheckCircle />,
  warning: <CheckCircle />,
};

export const NBSnackbar = forwardRef<HTMLDivElement, NBSnackbarProps>(
  // eslint-disable-next-line
  ({ id, message, iconVariant, persist, anchorOrigin, hideIconVariant, autoHideDuration, title, ...props }, ref) => {
    const handleDismiss = useCallback(() => {
      closeSnackbar(id);
    }, [id]);
    const snackbarVariant = props.variant ?? 'default';

    return (
      <SnackbarContent
        ref={ref}
        {...props}
        data-testid={`snackbar-${snackbarVariant}`}
        className={`${classes.snackbar} ${snackbarClasses[props.variant]} ${
          persist && pinnedSnackbarClasses[props.variant]
        }`}
      >
        {icon[props.variant]}
        <div
          className={classes.content}
          data-testid={`snackbar-content-${snackbarVariant}`}
        >
          <div className={`${classes.snackbarTitle}`}>{title}</div>
          <div className={`${classes.snackbarMessage}`}>{message}</div>
        </div>

        <IconButton
          className={classes.snackbarClose}
          data-testid={`snackbar-close-button-${snackbarVariant}`}
          onClick={handleDismiss}
        >
          <Close className={classes.snackbarClose} />
        </IconButton>
      </SnackbarContent>
    );
  },
);
